/* assets/provider-profile.js — GCITYPARTNERS-PROVIDER-PROFILE-04
 * Provider Profile editor child runtime for the canonical GCityPartners Provider Portal.
 * Browser identity uses LittleGWebSession native bearer; Provider approval/verification remain server-authoritative.
 */
(function (window, document) {
  'use strict';
  var VERSION = 'GCITYPARTNERS-PROVIDER-PROFILE-04';
  var KEY = '__GCITYPARTNERS_PROVIDER_PROFILE__';
  var ROOT = '[data-provider-profile]';
  var API_ORIGIN = 'https://api.glhksolution.com';
  var API_PREFIX = '/v1/gcitypartners/';
  var mounted = new WeakSet();

  if (window[KEY] && typeof window[KEY].mountAll === 'function') {
    window[KEY].mountAll(document);
    return;
  }

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function isZh() { return clean(document.documentElement.lang).toLowerCase().indexOf('zh') === 0; }
  function words(zh, en) { return isZh() ? zh : en; }
  function nativeSession() {
    var session = window.LittleGWebSession;
    return session && typeof session.authenticatedFetch === 'function' ? session : null;
  }
  function portalAllowed() {
    var session = nativeSession();
    if (!session || typeof session.getState !== 'function') return false;
    var state = session.getState();
    return Boolean(state && state.status === 'authenticated' && state.profile && clean(state.profile.id));
  }
  function endpoint(root) {
    var raw = clean(root.getAttribute('data-provider-profile-endpoint'));
    if (!raw) return '';
    try {
      var url = new URL(raw, API_ORIGIN);
      return url.origin === API_ORIGIN && url.pathname.indexOf(API_PREFIX) === 0 ? url.toString() : '';
    } catch (_error) { return ''; }
  }
  async function requestJson(url, options) {
    if (!url) throw new Error('missing_endpoint');
    var session = nativeSession();
    if (!session) throw new Error('native_session_unavailable');
    var init = Object.assign({ headers: { Accept: 'application/json' } }, options || {});
    if (init.body && typeof init.body !== 'string') {
      init.body = JSON.stringify(init.body);
      init.headers = Object.assign({}, init.headers, { 'Content-Type': 'application/json' });
    }
    var response = await session.authenticatedFetch(url, init);
    var payload = await response.json().catch(function () { return {}; });
    if (!response.ok || !payload || payload.ok !== true) {
      var error = new Error(clean(payload && payload.error) || clean(payload && payload.message) || 'request_failed');
      error.status = response.status;
      error.serverMessage = clean(payload && payload.message);
      throw error;
    }
    return payload;
  }
  function normalizeId(value) {
    return clean(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\u3400-\u9fff]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }
  function normalizeLanguage(value) {
    return clean(value)
      .toLowerCase()
      .replace(/_/g, '-')
      .replace(/[^a-z0-9-]+/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 35);
  }
  function splitValues(value, maxItems, normalizer) {
    var seen = Object.create(null);
    var limit = Math.max(1, Math.min(50, Math.floor(Number(maxItems || 50)) || 50));
    return clean(value).split(/[\n,]+/).map(normalizer).filter(function (item) {
      if (!item || seen[item]) return false;
      seen[item] = true;
      return true;
    }).slice(0, limit);
  }
  function splitIds(value, maxItems) { return splitValues(value, maxItems, normalizeId); }
  function splitLanguages(value) { return splitValues(value, 12, normalizeLanguage); }
  function joinIds(value) { return Array.isArray(value) ? value.join('\n') : ''; }
  function field(form, name) { return form && form.elements ? form.elements.namedItem(name) : null; }
  function setValue(form, name, value) {
    var node = field(form, name);
    if (!node || typeof node.value === 'undefined') return;
    node.value = value == null ? '' : String(value);
  }
  function setRadio(form, name, value) {
    var list = form ? form.querySelectorAll('input[name="' + name + '"]') : [];
    Array.prototype.forEach.call(list, function (node) { node.checked = node.value === value; });
  }
  function status(root, text) {
    var node = root.querySelector('[data-provider-profile-status]');
    if (node) node.textContent = text || '';
  }
  function minorDigits(currency) {
    var code = clean(currency).toUpperCase();
    if (['JPY', 'KRW'].indexOf(code) >= 0) return 0;
    if (['BHD', 'JOD', 'KWD', 'OMR', 'TND'].indexOf(code) >= 0) return 3;
    return 2;
  }
  function decimalToMinor(value, currency) {
    var raw = clean(value);
    if (!raw) return null;
    if (!/^\d+(?:\.\d+)?$/.test(raw)) throw new Error('invalid_minimum_project_size_minor');
    var digits = minorDigits(currency);
    var parts = raw.split('.');
    var whole = parts[0] || '0';
    var fractionRaw = parts[1] || '';
    if (fractionRaw.length > digits && /[1-9]/.test(fractionRaw.slice(digits))) throw new Error('minimum_project_size_precision');
    var fraction = fractionRaw.slice(0, digits).padEnd(digits, '0');
    var factor = Math.pow(10, digits);
    var minor = Number(whole) * factor + Number(fraction || 0);
    if (!Number.isSafeInteger(minor) || minor < 0) throw new Error('invalid_minimum_project_size_minor');
    return minor;
  }
  function minorToDecimal(value, currency) {
    if (!Number.isSafeInteger(Number(value)) || Number(value) < 0) return '';
    var digits = minorDigits(currency);
    if (!digits) return String(Number(value));
    return (Number(value) / Math.pow(10, digits)).toFixed(digits).replace(/\.?0+$/, '');
  }
  function render(root, profile) {
    var form = root.querySelector('[data-provider-profile-form]');
    if (!form) return;
    var data = profile && typeof profile === 'object' ? profile : {};
    setValue(form, 'legal_name', data.legal_name);
    setValue(form, 'display_name', data.display_name);
    setValue(form, 'website', data.website);
    setValue(form, 'region', data.region);
    setValue(form, 'public_summary', data.public_summary);
    setValue(form, 'company_size', data.company_size || 'unspecified');
    setValue(form, 'availability_status', data.availability_status || 'unknown');
    setValue(form, 'capability_ids', joinIds(data.capability_ids));
    setValue(form, 'service_regions', joinIds(data.service_regions));
    setValue(form, 'contact_name', data.contact_name);
    setValue(form, 'contact_role', data.contact_role);
    setValue(form, 'contact_email', data.contact_email);
    setValue(form, 'contact_phone', data.contact_phone);
    setValue(form, 'service_languages', joinIds(data.service_languages));
    setValue(form, 'preferred_currency', data.preferred_currency || 'HKD');
    setValue(form, 'minimum_project_size', minorToDecimal(data.minimum_project_size_minor, data.preferred_currency || 'HKD'));
    setValue(form, 'typical_response_time_hours', data.typical_response_time_hours);
    setValue(form, 'service_description', data.service_description);
    setValue(form, 'portfolio_url', data.portfolio_url);
    setRadio(form, 'visibility', data.visibility === 'public' ? 'public' : 'private');

    var approval = data.approval && typeof data.approval === 'object' ? data.approval : {};
    var approvalNode = root.querySelector('[data-provider-profile-approval]');
    if (approvalNode) approvalNode.textContent = words('審批：', 'Approval: ') + clean(approval.status || 'unknown');
    var verification = data.verification && typeof data.verification === 'object' ? data.verification : {};
    var verificationNode = root.querySelector('[data-provider-profile-verification]');
    if (verificationNode) verificationNode.textContent = words('核實：', 'Verification: ') + clean(verification.status || 'declared');
    var sourceNode = root.querySelector('[data-provider-profile-source]');
    if (sourceNode) {
      sourceNode.textContent = data.source_adapter === 'gtender_legacy_adapter'
        ? words('舊資料參考', 'Legacy reference')
        : clean(data.public_profile_id)
          ? words('已有公開識別', 'Public reference available')
          : words('尚未建立公開識別', 'No public reference yet');
    }
    var legacy = root.querySelector('[data-provider-profile-legacy]');
    if (legacy) legacy.hidden = data.source_adapter !== 'gtender_legacy_adapter';
  }
  function payload(form) {
    var selected = form.querySelector('input[name="visibility"]:checked');
    var currency = clean(field(form, 'preferred_currency') && field(form, 'preferred_currency').value).toUpperCase() || 'HKD';
    var responseRaw = clean(field(form, 'typical_response_time_hours') && field(form, 'typical_response_time_hours').value);
    return {
      legal_name: clean(field(form, 'legal_name') && field(form, 'legal_name').value),
      display_name: clean(field(form, 'display_name') && field(form, 'display_name').value),
      website: clean(field(form, 'website') && field(form, 'website').value),
      region: clean(field(form, 'region') && field(form, 'region').value),
      public_summary: clean(field(form, 'public_summary') && field(form, 'public_summary').value),
      company_size: clean(field(form, 'company_size') && field(form, 'company_size').value) || 'unspecified',
      availability_status: clean(field(form, 'availability_status') && field(form, 'availability_status').value) || 'unknown',
      visibility: selected ? selected.value : 'private',
      capability_ids: splitIds(field(form, 'capability_ids') && field(form, 'capability_ids').value, 50),
      service_regions: splitIds(field(form, 'service_regions') && field(form, 'service_regions').value, 30),
      contact_name: clean(field(form, 'contact_name') && field(form, 'contact_name').value),
      contact_role: clean(field(form, 'contact_role') && field(form, 'contact_role').value),
      contact_email: clean(field(form, 'contact_email') && field(form, 'contact_email').value),
      contact_phone: clean(field(form, 'contact_phone') && field(form, 'contact_phone').value),
      service_languages: splitLanguages(field(form, 'service_languages') && field(form, 'service_languages').value),
      minimum_project_size_minor: decimalToMinor(field(form, 'minimum_project_size') && field(form, 'minimum_project_size').value, currency),
      preferred_currency: currency,
      typical_response_time_hours: responseRaw ? Number(responseRaw) : null,
      service_description: clean(field(form, 'service_description') && field(form, 'service_description').value),
      portfolio_url: clean(field(form, 'portfolio_url') && field(form, 'portfolio_url').value)
    };
  }
  async function load(root) {
    if (!portalAllowed()) {
      root.setAttribute('data-provider-profile-state', 'guest');
      status(root, words('登入 GCityPartners 帳戶後可管理企業檔案。', 'Sign in to your GCityPartners account to manage the company profile.'));
      return;
    }
    root.setAttribute('data-provider-profile-state', 'loading');
    status(root, words('正在載入企業檔案…', 'Loading company profile…'));
    try {
      var result = await requestJson(endpoint(root));
      render(root, result.profile || null);
      root.setAttribute('data-provider-profile-state', 'ready');
      status(root, result.profile ? '' : words('建立企業檔案後才可公開被搜尋。', 'Create your company profile before publishing it.'));
    } catch (error) {
      root.setAttribute('data-provider-profile-state', 'error');
      status(root, clean(error && error.message) === 'provider_not_approved'
        ? words('此帳戶尚未獲准承接 Provider 工作。', 'This account is not approved for Provider work yet.')
        : words('暫時無法載入企業檔案。', 'Company profile could not be loaded.'));
    }
  }
  async function save(root, form) {
    var button = form.querySelector('button[type="submit"]');
    var body;
    try {
      body = payload(form);
    } catch (error) {
      status(root, clean(error && error.message) === 'minimum_project_size_precision'
        ? words('最低項目金額的小數位超出所選幣別允許精度。', 'Minimum project size has too many decimal places for the selected currency.')
        : words('請檢查最低項目金額。', 'Check the minimum project size.'));
      return;
    }
    if (!body.legal_name) {
      status(root, words('請填寫法定公司名稱。', 'Legal company name is required.'));
      return;
    }
    if (!body.capability_ids.length) {
      status(root, words('請至少填寫一項能力。', 'Add at least one capability.'));
      return;
    }
    root.setAttribute('data-provider-profile-state', 'saving');
    if (button) button.disabled = true;
    status(root, words('正在儲存…', 'Saving…'));
    try {
      var result = await requestJson(endpoint(root), {
        method: 'PUT',
        body: body
      });
      var saved = result.profile;
      var confirmed = saved
        && typeof saved === 'object'
        && !Array.isArray(saved)
        && clean(saved.legal_name)
        && Array.isArray(saved.capability_ids)
        && ['public', 'private'].includes(saved.visibility)
        && saved.source_adapter !== 'gtender_legacy_adapter';

      if (!confirmed) {
        root.setAttribute('data-provider-profile-state', 'error');
        status(root, words(
          '伺服器已回應，但未能確認儲存後的檔案。輸入仍保留；請重新載入核對，勿立即重複提交。',
          'The server responded, but the saved profile could not be confirmed. Your input is preserved; reload to check before submitting again.'
        ));
        return;
      }

      render(root, saved);
      root.setAttribute('data-provider-profile-state', 'ready');
      status(root, saved.visibility === 'public'
        ? words(
            '已儲存。伺服器返回公開狀態；搜尋結果仍需核對。申報能力不等於已核實資質。',
            'Saved. The server returned public visibility; search discoverability still needs checking. Declared capability is not verified qualification.'
          )
        : words(
            '已儲存。伺服器返回私人狀態。',
            'Saved. The server returned private visibility.'
          ));
    } catch (error) {
      root.setAttribute('data-provider-profile-state', 'error');
      var code = clean(error && error.message);
      var message = code === 'invalid_website'
        ? words('網站必須使用有效 HTTPS 網址。', 'Website must be a valid HTTPS URL.')
        : code === 'invalid_portfolio_url'
          ? words('作品集必須使用有效 HTTPS 網址。', 'Portfolio must be a valid HTTPS URL.')
          : code === 'invalid_contact_email'
            ? words('請填寫有效聯絡電郵。', 'Enter a valid contact email.')
            : words('暫時無法儲存企業檔案。', 'Company profile could not be saved.');
      status(root, message);
    } finally {
      if (button && document.contains(button)) button.disabled = false;
    }
  }
  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    var form = root.querySelector('[data-provider-profile-form]');
    if (form) form.addEventListener('submit', function (event) { event.preventDefault(); save(root, form); });
    load(root);
  }
  function mountAll(scope) { Array.prototype.forEach.call((scope || document).querySelectorAll(ROOT), mount); }
  function refresh(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll(ROOT),
      function (root) {
        if (mounted.has(root)) load(root);
        else mount(root);
      }
    );
  }

  var api = Object.freeze({
    version: VERSION,
    mountAll: mountAll,
    refresh: refresh,
    splitIds: splitIds,
    splitLanguages: splitLanguages,
    normalizeId: normalizeId,
    decimalToMinor: decimalToMinor
  });
  window[KEY] = api;
  window.GCityProviderProfile = api;
  document.addEventListener('littleg:web-session-change', function () { refresh(document); });
  document.addEventListener('shopify:section:load', function (event) { refresh(event.target || document); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mountAll(document); }, { once: true });
  else mountAll(document);
})(window, document);
